import type { Operation, TransformInput } from '@mystiq/contracts';
import { useRef, useState } from 'react';
import { useFolders, useWorkspaceMutation } from '../api/hooks.ts';
import { useApi } from '../auth/session.tsx';
import { folderPaths } from './MoveDialog.tsx';
import { ErrorLine, Modal } from './ui.tsx';

const KINDS: Array<{ kind: TransformInput['kind']; label: string; hint: string; speech?: true }> = [
  { kind: 'summary', label: 'Summary', hint: 'The main points, decisions, numbers and dates.' },
  { kind: 'clean_transcript', label: 'Clean transcript', hint: 'Every word kept, without filler, in readable paragraphs.', speech: true },
  { kind: 'meeting_notes', label: 'Meeting notes', hint: 'Decisions, action items and open questions.', speech: true },
  { kind: 'brief', label: 'Brief', hint: 'One page: purpose, key facts, implications, next steps.' },
  { kind: 'report', label: 'Report', hint: 'Overview, findings, details and open questions.' },
  { kind: 'notes', label: 'Notes', hint: 'Organized notes that keep every detail.' },
  { kind: 'custom', label: 'Something else', hint: 'Describe what you want below.' },
];

/**
 * Asks AI to write a new note from the chosen items. The note is independent:
 * it stays when the sources change or go to Trash, and Related links back.
 */
export function TransformDialog({
  ws,
  sourceItemIds,
  speech = false,
  onClose,
  onRequested,
}: {
  ws: string;
  sourceItemIds: string[];
  /** The sources are recordings or transcripts: transcript cleanup and meeting notes are offered too. */
  speech?: boolean;
  onClose(): void;
  onRequested(operation: Operation): void;
}) {
  const api = useApi();
  const folders = useFolders(ws);
  const [kind, setKind] = useState<TransformInput['kind']>('summary');
  const [instructions, setInstructions] = useState('');
  const [title, setTitle] = useState('');
  const [folderId, setFolderId] = useState('');
  const write = useWorkspaceMutation(ws, (input: TransformInput) => api.transform(ws, input));
  // Asking again with the same choices (say, after a lost response) repeats the same request, never a second paid run.
  const lastRequest = useRef<{ payload: string; key: string } | null>(null);
  const needsInstructions = kind === 'custom' && !instructions.trim();
  const count = sourceItemIds.length;

  return (
    <Modal title={count > 1 ? `Write from ${count} items` : 'Write with AI'} onClose={onClose}>
      <fieldset className="field">
        <legend className="field-label">What to write</legend>
        <div className="stack">
          {KINDS.filter((option) => speech || !option.speech).map((option) => (
            <label key={option.kind} className="choice">
              <input type="radio" name="transform-kind" value={option.kind} checked={kind === option.kind} onChange={() => setKind(option.kind)} />
              <span>
                <span className="row-title">{option.label}</span> <span className="meta">{option.hint}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <label className="field">
        <span className="field-label">{kind === 'custom' ? 'Instructions' : 'Instructions (optional)'}</span>
        <textarea
          className="textarea textarea-short"
          maxLength={4000}
          value={instructions}
          placeholder={kind === 'custom' ? 'For example: turn this into a FAQ for new volunteers' : 'For example: focus on the budget'}
          onChange={(event) => setInstructions(event.target.value)}
        />
      </label>
      <label className="field">
        <span className="field-label">Title (optional)</span>
        <input className="input" value={title} maxLength={200} placeholder="AI suggests one" onChange={(event) => setTitle(event.target.value)} />
      </label>
      <label className="field">
        <span className="field-label">Save to</span>
        <select className="select" value={folderId} onChange={(event) => setFolderId(event.target.value)}>
          <option value="">{count > 1 ? 'Same folder as the items (or Inbox)' : 'Same folder'}</option>
          {folderPaths(folders.data ?? []).map((folder) => (
            <option key={folder.id} value={folder.id}>
              {folder.path}
            </option>
          ))}
        </select>
      </label>
      <p className="meta">The result is a new note of its own. It stays even if the sources change or go to Trash.</p>
      <ErrorLine error={write.error} />
      <div className="modal-actions">
        <button
          className="button button-primary"
          disabled={write.isPending || needsInstructions}
          onClick={async () => {
            const request: TransformInput = {
              sourceItemIds,
              kind,
              ...(instructions.trim() ? { instructions: instructions.trim() } : {}),
              ...(title.trim() ? { title: title.trim() } : {}),
              ...(folderId ? { folderId } : {}),
            };
            const payload = JSON.stringify(request);
            if (lastRequest.current?.payload !== payload) lastRequest.current = { payload, key: crypto.randomUUID() };
            const operation = await write.mutateAsync({ ...request, idempotencyKey: lastRequest.current.key }).catch(() => null);
            if (operation) {
              onRequested(operation);
              onClose();
            }
          }}
        >
          Write
        </button>
        <button className="button" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
